import { useEffect, useId, useState } from "react";
import * as api from "../../api";
import Dialogo from "./Dialogo";
import SelectorDestinatario from "./SelectorDestinatario";

const PRIORIDADES = [[0, "Baja"], [1, "Media"], [2, "Alta"], [3, "Urgente"]];
const ESTIMACIONES = [["", "Sin estimar"], [30, "30 min"], [60, "1 h"], [120, "2 h"], [180, "3 h"], [240, "4 h"], [480, "1 día (8 h)"]];

// «Mandar tarea»: una tarea suelta para alguien de TU equipo. Entra directa en su lista, marcada
// «Enviada por ti», sin que tenga que aceptarla.
// receptores: lo que da /encargos/receptores (puede ser toda la plantilla); equiposMios: los equipos de quien manda, para
// agrupar primero el suyo y avisar si la persona es de otro.
//
// modo "proponer" (quien no es gestor ni admin, T-136): la misma ventana, pero la tarea la aprueba un gestor antes de que la
// reciba nadie. `receptores` son entonces las personas a las que puede proponer (el, con el nombre «Yo mismo», y su equipo),
// `miId` dice cual es el y `inicial` rellena titulo y descripcion al «Proponer otra».
export default function MandarTareaModal({ receptores = [], total = null, alcance = "equipo", equiposMios = [], onCerrar, onEnviada,
  modo = "mandar", miId = null, inicial = null }) {
  const proponer = modo === "proponer";
  const idDest = useId();
  const [receptor, setReceptor] = useState(() => (proponer ? receptores.find(r => String(r.id) === String(miId)) || null : null));
  const [titulo, setTitulo] = useState(inicial?.titulo || "");
  const [descripcion, setDescripcion] = useState(inicial?.descripcion || "");
  const [fecha, setFecha] = useState("");
  const [prioridad, setPrioridad] = useState(1);
  const [estimado, setEstimado] = useState("");
  const [proyectoId, setProyectoId] = useState("");
  const [proyectos, setProyectos] = useState([]);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.getProyectos({}).then(r => { if (vivo && Array.isArray(r)) setProyectos(r.filter(p => p?.id != null && p.name)); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  // Una persona de otro equipo se puede elegir (el servidor ya decidio que se puede), pero se dice, sin bloquear
  const deOtroEquipo = !!receptor && alcance === "todos" && equiposMios.length > 0
    && !(receptor.equipos || []).some(x => equiposMios.includes(x));

  const hoy = new Date();
  const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;

  const enviar = async e => {
    e.preventDefault();
    setError("");
    if (!receptor) return setError("Elige a quién se la mandas");
    if (!titulo.trim()) return setError("La tarea necesita un título");
    setEnviando(true);
    try {
      const r = await (proponer ? api.proponerEncargo : api.mandarEncargo)({
        receptor_id: receptor.id, titulo: titulo.trim(), descripcion: descripcion.trim() || undefined,
        fecha_limite: fecha || undefined, prioridad,
        tiempo_estimado_min: estimado === "" ? undefined : Number(estimado),
        project_id: proyectoId || undefined,
      });
      onEnviada?.(r, receptor);
    } catch (err) {
      setError(err?.message || (proponer ? "No se ha podido proponer la tarea" : "No se ha podido mandar la tarea"));
      setEnviando(false);
    }
  };

  return (
    <Dialogo titulo={proponer ? "Proponer tarea" : "Mandar tarea"} onCerrar={onCerrar}>
      <form className="mt-form" onSubmit={enviar} noValidate>
        {/* Con persona elegida ya no hay campo al que apuntar: la etiqueta pasa a ser texto */}
        {receptor
          ? <span className="mt-lb">{alcance === "todos" || proponer ? "Para quién" : "Para quién (tu equipo)"}</span>
          : <label className="mt-lb" htmlFor={idDest}>{alcance === "todos" || proponer ? "Para quién" : "Para quién (tu equipo)"}</label>}
        <SelectorDestinatario inputId={idDest} receptores={receptores} total={total} equiposMios={equiposMios} value={receptor} onChange={setReceptor} />
        {proponer && <p className="mt-sel-nota">Para alguien de otro equipo, pídeselo a un gestor o administrador.</p>}
        {deOtroEquipo && <p className="mt-sel-nota" role="status">Esta persona no es de tu equipo.</p>}

        <label className="mt-lb" htmlFor="mt-tit">Título</label>
        <input id="mt-tit" className="mt-inp" maxLength={300} value={titulo} onChange={e => setTitulo(e.target.value)} />
        <label className="mt-lb" htmlFor="mt-desc">Descripción</label>
        <textarea id="mt-desc" className="mt-inp" rows={3} maxLength={5000} value={descripcion} onChange={e => setDescripcion(e.target.value)} />

        <div className="mt-dos">
          <div>
            <label className="mt-lb" htmlFor="mt-fecha">Fecha límite</label>
            <input id="mt-fecha" type="date" className="mt-inp" min={hoyIso} value={fecha} onChange={e => setFecha(e.target.value)} />
          </div>
          <div>
            <label className="mt-lb" htmlFor="mt-est">Tiempo estimado (opcional)</label>
            <select id="mt-est" className="mt-inp" value={estimado} onChange={e => setEstimado(e.target.value)}>
              {ESTIMACIONES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
        </div>

        <span className="mt-lb" id="mt-prio">Prioridad</span>
        <div className="mt-plan-rap" role="group" aria-labelledby="mt-prio">
          {PRIORIDADES.map(([v, t]) => (
            <button key={v} type="button" className="mt-chip-b" aria-pressed={prioridad === v} onClick={() => setPrioridad(v)}>{t}</button>
          ))}
        </div>

        {proyectos.length > 0 && (
          <>
            <label className="mt-lb" htmlFor="mt-proy">Proyecto (opcional)</label>
            <select id="mt-proy" className="mt-inp" value={proyectoId} onChange={e => setProyectoId(e.target.value)}>
              <option value="">Sin proyecto</option>
              {proyectos.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </>
        )}

        {proponer
          ? <div className="mt-plan-aviso" role="note"><span>Un gestor o administrador tendrá que aprobarla. Hasta entonces la verá quien la reciba, pero no podrá planificarla ni empezarla. Te avisaremos cuando se resuelva.</span></div>
          : <p className="mt-sel-nota">{receptor ? `${receptor.nombre.split(" ")[0]} la verá en su lista como «Enviada por ti» y recibirá un aviso. No necesita aceptarla.` : "La persona la verá en su lista como «Enviada por ti» y recibirá un aviso. No necesita aceptarla."}</p>
        }
        {error && <div className="mt-plan-aviso" role="alert"><span>{error}</span></div>}
        <div className="mt-dlg-pie">
          <button type="button" className="mt-b btn" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="mt-b p btn" disabled={enviando}>{enviando ? (proponer ? "Proponiendo…" : "Mandando…") : (proponer ? "Proponer tarea" : "Mandar tarea")}</button>
        </div>
      </form>
    </Dialogo>
  );
}
